import React from 'react';
import styled from 'styled-components';
import { BRAND_COLOURS } from '../../fundamentals/colours/variants';
import { PX_TO_REM } from '../../fundamentals/sizes/sizes';
import { IGutterAsMarginProps, useGutterAsMarginCss } from '../../fundamentals/spaces/space';
import { FONT_WEIGHTS, FONT_SIZES } from '../../fundamentals/typography/typography';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Span } from '../Texts/Texts';
import { generateProgressBarColours } from '../../fundamentals/colours/generateUiColours';
import { IColoursCollection } from '../../fundamentals/colours/colourInterfaces';
import { BaseDiv } from '../../common/BaseElements';

export type ProgressBarLabelPosition = 'right' | 'below' | 'none';
export interface IProgressBarProps extends IGutterAsMarginProps {
  progress?: number;
  label?: string;
  labelPosition?: ProgressBarLabelPosition;
  progressDecimalPlaces?: number;
}

export const progressBarColours = (collection: IColoursCollection) => ({
  progressBarColours: generateProgressBarColours(collection)
});

const MIN_PROGRESS = 0;
const MAX_PROGRESS = 1;

const Container = styled(BaseDiv)<IProgressBarProps>`
  ${useGutterAsMarginCss()};
  display: flex;
  align-items: ${({ labelPosition }) => (labelPosition === 'below' ? 'start' : 'center')};
  flex-direction: ${({ labelPosition }) => (labelPosition === 'below' ? 'column' : 'row')};
  width: 100%;
`;

const BackgroundBar = styled(BaseDiv)`
  height: ${PX_TO_REM[8]};
  width: 100%;
  background: ${({ theme }) => theme.progressBarColours[BRAND_COLOURS.ACCENT].background};
  border-radius: ${PX_TO_REM[4]};
`;

const Bar = styled(BackgroundBar)<Required<Pick<IProgressBarProps, 'progress'>>>`
  width: ${({ progress }) => `${progress * 100}%`};
  background: ${({ theme }) => theme.progressBarColours[BRAND_COLOURS.ACCENT].text};
`;

export const ProgressBar: React.FC<IProgressBarProps> = ({ label, labelPosition = 'below', progress = 0, progressDecimalPlaces = 0, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'ProgressBar');

  progress = Math.min(Math.max(progress, MIN_PROGRESS), MAX_PROGRESS);

  if (!label && labelPosition !== 'none') {
    // no label provided, but label position is not none, use default (0% - 100%)
    label = `${(progress * 100).toFixed(progressDecimalPlaces)}%`;
  }

  return (
    <Container labelPosition={labelPosition} {...rest} {...generateTestId()}>
      <BackgroundBar {...rest} {...generateTestId('BackgroundBar')}>
        <Bar progress={progress} {...rest} {...generateTestId(`Bar.${progress}`)}></Bar>
      </BackgroundBar>
      {labelPosition !== 'none' && (
        <Span
          textSize={FONT_SIZES.TEXT_SMALL}
          fontWeightCss={FONT_WEIGHTS.REGULAR}
          gutterAsMargin={true}
          gutterLeft={labelPosition === 'right' ? 8 : false}
          gutterTop={labelPosition === 'below' ? 4 : false}
          {...generateTestId('Label')}
        >
          {label}
        </Span>
      )}
    </Container>
  );
};
